/**
 * Pure helpers for "can this Adjust & Lock row be locked right now?"
 * Lives in its own module (no React, no axios) so it can be unit-tested
 * without pulling the whole hook graph.
 *
 * Backend stamps `can_lock_now` / `lock_block_reason` on each calendar
 * day. This helper prefers those fields and falls back to the same
 * product rule when an older payload is missing them:
 *
 * Past dates are always lockable. Future dates never are. Today is
 * lockable for approved full-day true-absence leave (`day_type ===
 * 'leave'`) or when the backend confirms an employee's final AM/PM
 * employment boundary has passed and the last attendance session is
 * closed. Other working, rest, and holiday rows stay blocked.
 */

export const TODAY_LOCK_ERROR =
  'Cannot lock today — the workday has not concluded. Approved full-day leave or a concluded final employment session can be locked today.';

export const FUTURE_LOCK_ERROR = 'Cannot lock future dates.';

export const isAttendanceConcludedToday = (rec) => {
  if (!rec) return false;
  if (typeof rec.can_lock_now === 'boolean' && rec.date) {
    // Backend already decided; "concluded today" is only meaningful
    // for the two explicit same-day completion paths.
    return rec.can_lock_now && (
      rec.day_type === 'leave'
      || rec.lock_ready_code === 'final_employment_session_concluded'
    );
  }
  return rec.day_type === 'leave';
};

/**
 * Whether this attendance row can be locked right now.
 *
 * @param {object} rec - daily record from the attendance calendar
 * @param {string} todayIso - today's date in SGT as YYYY-MM-DD
 * @returns {boolean}
 */
export const isDayLockableNow = (rec, todayIso) => {
  if (!rec?.date || rec.isPreEmployment || rec.isPostEmployment) return false;
  if (typeof rec.can_lock_now === 'boolean') return rec.can_lock_now;
  if (rec.date < todayIso) return true;
  if (rec.date > todayIso) return false;
  return rec.day_type === 'leave';
};

/**
 * Whether HR can re-normalize this row from current clock events.
 *
 * Past unlocked days are always eligible. Today is eligible only once
 * the same-day lock rule says the day has concluded (approved full-day
 * leave or a closed final employment session).
 *
 * @param {object} rec - daily record from the attendance calendar
 * @param {string} todayIso - today's date in SGT as YYYY-MM-DD
 * @returns {boolean}
 */
export const isDayRenormalizableNow = (rec, todayIso) => {
  if (!rec?.date || rec.isPreEmployment || rec.isPostEmployment) return false;
  if (rec.lock_status === 'locked') return false;
  if (rec.date < todayIso) return true;
  if (rec.date > todayIso) return false;
  return isDayLockableNow(rec, todayIso);
};

/**
 * Human-readable reason when a row cannot be locked.
 *
 * @param {object} rec - daily record from the attendance calendar
 * @param {string} todayIso - today's date in SGT as YYYY-MM-DD
 * @returns {string}
 */
export const getLockBlockReason = (rec, todayIso) => {
  if (rec?.lock_block_reason) return rec.lock_block_reason;
  if (!rec?.date || rec.date > todayIso) return FUTURE_LOCK_ERROR;
  return TODAY_LOCK_ERROR;
};
